import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  ArrowLeft,
  Loader2,
  Calendar,
  Building2,
  CreditCard,
  BarChart3,
  PieChart,
  Receipt
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Layout from '../../components/Layout';
import api from '../../lib/api';

const RevenuePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState(null);
  const [billingData, setBillingData] = useState(null);
  const [trends, setTrends] = useState([]);

  useEffect(() => {
    fetchRevenueData();
  }, []);

  const fetchRevenueData = async () => {
    try {
      setLoading(true);
      const [statsRes, billingRes, trendsRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/admin/billing/overview'),
        api.get('/admin/analytics/trends')
      ]);
      setRevenueData(statsRes.data);
      setBillingData(billingRes.data);
      setTrends(trendsRes.data.trends || []);
    } catch (error) {
      console.error('Failed to fetch revenue data:', error);
      toast.error('Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  };

  // Redirect non-super-admins
  if (user?.role !== 'super_admin') {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-red-400">Access denied. Super Admin only.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </Layout>
    );
  }

  const planPrices = {
    trial: 0,
    free: 0,
    basic: 49,
    pro: 99,
    enterprise: 299
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-['Barlow_Condensed'] text-3xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
                <DollarSign className="w-8 h-8 text-green-500" />
                Revenue Analytics
              </h1>
              <p className="text-slate-400">Track your monthly and annual recurring revenue</p>
            </div>
          </div>
        </div>

        {/* Main Revenue Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card-industrial p-6">
            <div className="flex items-center justify-between mb-4">
              <DollarSign className="w-8 h-8 text-green-500" />
              <span className="text-xs text-slate-500 uppercase">This Month</span>
            </div>
            <p className="text-slate-400 text-sm">Monthly Recurring Revenue</p>
            <p className="text-4xl font-bold text-green-400">${revenueData?.financials?.mrr?.toLocaleString() || 0}</p>
            <p className="text-slate-500 text-sm mt-2">MRR</p>
          </div>

          <div className="card-industrial p-6">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="w-8 h-8 text-green-500" />
              <span className="text-xs text-slate-500 uppercase">Projected</span>
            </div>
            <p className="text-slate-400 text-sm">Annual Recurring Revenue</p>
            <p className="text-4xl font-bold text-green-400">${revenueData?.financials?.arr?.toLocaleString() || 0}</p>
            <p className="text-slate-500 text-sm mt-2">ARR</p>
          </div>

          <div className="card-industrial p-6">
            <div className="flex items-center justify-between mb-4">
              <CreditCard className="w-8 h-8 text-cyan-500" />
              <span className="text-xs text-slate-500 uppercase">Active</span>
            </div>
            <p className="text-slate-400 text-sm">Paying Customers</p>
            <p className="text-4xl font-bold text-cyan-400">{revenueData?.companies?.paying || 0}</p>
            <p className="text-slate-500 text-sm mt-2">subscriptions</p>
          </div>

          <div className="card-industrial p-6">
            <div className="flex items-center justify-between mb-4">
              <BarChart3 className="w-8 h-8 text-amber-500" />
              <span className="text-xs text-slate-500 uppercase">Avg</span>
            </div>
            <p className="text-slate-400 text-sm">Avg Revenue Per User</p>
            <p className="text-4xl font-bold text-amber-400">
              ${revenueData?.companies?.paying > 0 
                ? Math.round(revenueData?.financials?.mrr / revenueData?.companies?.paying) 
                : 0}
            </p>
            <p className="text-slate-500 text-sm mt-2">ARPU</p>
          </div>
        </div>

        {/* MRR Trend Chart */}
        <div className="card-industrial p-6">
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-500" />
            Monthly Recurring Revenue Trend
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis 
                  dataKey="month" 
                  stroke="#94a3b8"
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  stroke="#94a3b8"
                  style={{ fontSize: '12px' }}
                  tickFormatter={(value) => `$${value}`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1e293b', 
                    border: '1px solid #334155',
                    borderRadius: '8px'
                  }}
                  labelStyle={{ color: '#f1f5f9' }}
                  formatter={(value) => [`$${value}`, 'MRR']}
                />
                <Line 
                  type="monotone" 
                  dataKey="mrr" 
                  stroke="#10b981" 
                  strokeWidth={3}
                  dot={{ fill: '#10b981', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Revenue Breakdown by Plan */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card-industrial p-6">
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-amber-500" />
              Revenue by Plan
            </h2>
            <div className="space-y-4">
              {Object.entries(planPrices).map(([plan, price]) => {
                const count = revenueData?.plans?.[plan] || 0;
                const revenue = count * price;
                const percentage = revenueData?.financials?.mrr > 0 
                  ? Math.round((revenue / revenueData?.financials?.mrr) * 100) 
                  : 0;
                
                return (
                  <div key={plan} className="bg-slate-900 p-4 rounded">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white capitalize font-medium">{plan}</span>
                      <span className="text-green-400 font-bold">${revenue.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex-1 bg-slate-700 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            plan === 'enterprise' ? 'bg-green-500' :
                            plan === 'pro' ? 'bg-purple-500' :
                            plan === 'basic' ? 'bg-blue-500' :
                            'bg-slate-500'
                          }`}
                          style={{ width: `${Math.min(percentage, 100)}%` }}
                        />
                      </div>
                      <span className="text-slate-400 text-sm w-12 text-right">{percentage}%</span>
                    </div>
                    <p className="text-slate-500 text-xs mt-1">{count} customers × ${price}/mo</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card-industrial p-6">
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-amber-500" />
              Revenue Summary
            </h2>
            <div className="space-y-4">
              <div className="bg-slate-900 p-4 rounded">
                <p className="text-slate-400 text-sm">Total Customers</p>
                <p className="text-2xl font-bold text-white">{revenueData?.companies?.total || 0}</p>
              </div>
              <div className="bg-slate-900 p-4 rounded">
                <p className="text-slate-400 text-sm">Paying Customers</p>
                <p className="text-2xl font-bold text-green-400">{revenueData?.companies?.paying || 0}</p>
              </div>
              <div className="bg-slate-900 p-4 rounded">
                <p className="text-slate-400 text-sm">Conversion Rate</p>
                <p className="text-2xl font-bold text-cyan-400">
                  {revenueData?.companies?.total > 0 
                    ? Math.round((revenueData?.companies?.paying / revenueData?.companies?.total) * 100) 
                    : 0}%
                </p>
              </div>
              <div className="bg-slate-900 p-4 rounded">
                <p className="text-slate-400 text-sm">Trial Customers</p>
                <p className="text-2xl font-bold text-amber-400">{revenueData?.companies?.trial || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex gap-4">
          <Button
            onClick={() => navigate('/super-admin?tab=billing')}
            className="btn-primary"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Manage Billing
          </Button>
          <Button
            onClick={() => navigate('/super-admin/subscriptions')}
            className="btn-secondary"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            View Subscriptions
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default RevenuePage;
